import { Button, Checkbox, FormControlLabel } from "@mui/material"
import { ArrowLeft, X } from "lucide-mui"
import React, { useState } from "react"
import ReactMarkdown from "react-markdown"

import BookingPolicyTileIcon from "@/components/booking_policy_tile_icon"
import i18n from "@/i18n"
import Logo from "@/pages/public/logo"
import {
  getBookingPolicyTileTitle,
  type BookingPolicyTileViewModel,
  type BookingPolicySectionResult,
} from "@/shared/booking_policy"

import { STYLES as SHARED_STYLES } from "./_styles"
import Footer from "./footer"

export type BookingPolicyStepProps = {
  holdDurationSeconds?: number | null
  bookingPolicyTiles: BookingPolicyTileViewModel[]
  bookingPolicySection?: BookingPolicySectionResult | null
  mobileSheet?: boolean
  onBack: () => void
  onNext: () => void
  onClose: () => void
}

const STYLES = {
  tileGrid: "grid grid-cols-2 gap-x-8 gap-y-4",
  tile: "flex items-start gap-3",
  tileIcon: "flex h-8 w-8 shrink-0 items-center justify-center rounded-[3px] bg-[#f5f7f9] text-[#97a0ad]",
  tileTitle: "text-[12px] font-medium leading-[17px] text-black",
  tileSubtitle: "mt-0 text-[10px] leading-[14px] text-[#616161]",
  rulesTitle: "mb-3 mt-0 text-[14px] font-medium leading-[14px] tracking-[-0.5px] text-[#171717]",
  markdown:
    "prose prose-sm max-w-none text-[12px] leading-[18px] text-[#5b6779] opacity-90 prose-p:my-0 prose-ul:my-0 prose-li:my-0 prose-strong:text-[#444] prose-strong:font-bold",
  checkboxIcon: "block h-[18px] w-[18px] rounded-[4px] border border-[#d8d8d8] bg-white",
  checkboxCheckedIcon:
    "flex h-[18px] w-[18px] items-center justify-center rounded-[4px] border border-[#2596fa] bg-[#2596fa] text-white",
} as const

const DESKTOP_STYLES = {
  header: "border-b border-[#f7f7f9] px-5 py-[15px]",
  title: "m-0 text-[16px] font-medium tracking-[-0.5px] text-[#201f1f]",
  holdDuration: "bg-[#f5f7f9] px-5 py-2 text-center text-[12px] leading-[18px] text-[#6b7280]",
  holdDurationStrong: "font-semibold text-[#414142]",
  contentArea: "overflow-y-auto border-l border-[#e5e6ec] bg-white px-5 py-5",
  footerContainer: "bg-[#f5f7f9]",
} as const

const MOBILE_STYLES = {
  ...SHARED_STYLES,
  rootContainer: "flex h-full min-h-0 flex-col",
  holdDuration: "bg-[#f5f7f9] px-5 py-2.5 text-center text-[10px] leading-[14px] text-[#6b7280]",
  holdDurationStrong: "font-semibold text-[#5f6774]",
  contentContainer: "px-5 py-5 space-y-6",
  checkboxContainer: "mx-5 rounded-[5px] bg-[#f5f7f9] px-5 py-2.5 flex items-center gap-3",
  checkboxLabel: "text-[14px] font-medium leading-[normal] text-black",
  nextButton: "w-full h-10 rounded-[5px] bg-[#2596fa] text-base font-medium text-white normal-case",
  backButton:
    "w-full h-[34px] rounded-[5px] border border-[#eaeaea] bg-white text-base font-medium text-[#545a65] normal-case",
  footerContainer: "border-t border-[#f1f2f5] px-5 py-5 space-y-3",
} as const

const TileGrid: React.FC<{ tiles: BookingPolicyTileViewModel[] }> = ({ tiles }) => {
  if (!tiles.length) return null

  return (
    <div className={STYLES.tileGrid} data-testid="booking-policy-step-tiles">
      {tiles.map((tile) => (
        <div key={tile.kind} className={STYLES.tile} data-testid={`booking-policy-step-tile-${tile.kind}`}>
          <div className={STYLES.tileIcon}>
            <BookingPolicyTileIcon kind={tile.kind} className="h-[18px] w-[18px]" />
          </div>
          <div className="min-w-0">
            <div className={STYLES.tileTitle}>{getBookingPolicyTileTitle(tile.kind)}</div>
            <div className={STYLES.tileSubtitle}>{tile.subtitle}</div>
          </div>
        </div>
      ))}
    </div>
  )
}

const RulesContent: React.FC<{ section?: BookingPolicySectionResult | null }> = ({ section }) => {
  if (!section?.content?.trim()) return null

  return (
    <div data-testid="booking-policy-step-rules">
      <h3 className={STYLES.rulesTitle}>{section.title || i18n.t("app.public.venue.modal.reservation_rules")}</h3>
      <div className={STYLES.markdown}>
        <ReactMarkdown>{section.content}</ReactMarkdown>
      </div>
    </div>
  )
}

const BookingPolicyStepDesktop: React.FC<BookingPolicyStepProps> = ({
  holdDurationSeconds,
  bookingPolicyTiles,
  bookingPolicySection,
  onBack,
  onNext,
}) => {
  const holdDurationMinutes = Math.max(1, Math.round((holdDurationSeconds ?? 5 * 60) / 60))

  return (
    <>
      <div className={DESKTOP_STYLES.header} data-testid="booking-policy-step-header">
        <h2 className={DESKTOP_STYLES.title}>{i18n.t("app.public.venue.modal.booking_policy_title")}</h2>
      </div>

      <div className={DESKTOP_STYLES.holdDuration} data-testid="booking-policy-step-hold-duration">
        <span>{`${i18n.t("app.public.venue.modal.hold_duration_prefix")} `}</span>
        <strong className={DESKTOP_STYLES.holdDurationStrong}>
          {i18n.t("app.date.duration.minutes", { count: holdDurationMinutes })}
        </strong>
        <span>.</span>
      </div>

      <div className={DESKTOP_STYLES.contentArea} data-testid="booking-policy-step-content">
        <div className="space-y-6">
          <TileGrid tiles={bookingPolicyTiles} />
          <RulesContent section={bookingPolicySection} />
        </div>
      </div>

      <Footer
        canGoNext
        loading={false}
        onBack={onBack}
        onNext={onNext}
        containerClassName={DESKTOP_STYLES.footerContainer}
        showTopBorder={false}
      />
    </>
  )
}

const BookingPolicyStepMobile: React.FC<BookingPolicyStepProps> = ({
  holdDurationSeconds,
  bookingPolicyTiles,
  bookingPolicySection,
  onBack,
  onNext,
  onClose,
}) => {
  const [agreed, setAgreed] = useState(false)
  const holdDurationMinutes = Math.max(1, Math.round((holdDurationSeconds ?? 5 * 60) / 60))

  return (
    <div className={MOBILE_STYLES.rootContainer}>
      <div className={MOBILE_STYLES.mobileSheetHeader}>
        <button
          type="button"
          onClick={onBack}
          className={MOBILE_STYLES.mobileSheetBackBtn}
          aria-label={i18n.t("app.public.venue.modal.back")}
          data-testid="mobile-sheet-back-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h2 className={MOBILE_STYLES.mobileSheetTitle}>{i18n.t("app.public.venue.modal.booking_policy_title")}</h2>
        <button
          type="button"
          onClick={onClose}
          className={MOBILE_STYLES.mobileSheetCloseBtn}
          aria-label={i18n.t("app.public.venue.modal.close")}
          data-testid="mobile-sheet-close-btn"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className={MOBILE_STYLES.holdDuration} data-testid="booking-policy-step-hold-duration">
        <span>{`${i18n.t("app.public.venue.modal.hold_duration_prefix")} `}</span>
        <strong className={MOBILE_STYLES.holdDurationStrong}>
          {i18n.t("app.date.duration.minutes", { count: holdDurationMinutes })}
        </strong>
        <span>.</span>
      </div>

      <div className={MOBILE_STYLES.mobileSheetContent}>
        <div className={MOBILE_STYLES.contentContainer} data-testid="booking-policy-step-content">
          <TileGrid tiles={bookingPolicyTiles} />
          <RulesContent section={bookingPolicySection} />
        </div>

        <div className={MOBILE_STYLES.checkboxContainer} data-testid="booking-policy-step-agree-checkbox">
          <FormControlLabel
            control={
              <Checkbox
                checked={agreed}
                onChange={(event) => setAgreed(event.target.checked)}
                disableRipple
                size="small"
                icon={<span className={STYLES.checkboxIcon} />}
                checkedIcon={
                  <span className={STYLES.checkboxCheckedIcon}>
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                      <path
                        d="M2.5 6.5L5.5 9.5L10.5 3.5"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                }
                sx={{
                  p: 0,
                  color: "transparent",
                  "&.Mui-checked": { color: "transparent" },
                  "&:hover": { bgcolor: "transparent" },
                }}
              />
            }
            label={i18n.t("app.public.venue.modal.agree_booking_policy")}
            sx={{
              m: 0,
              gap: "12px",
              "& .MuiFormControlLabel-label": { fontSize: "14px", fontWeight: 500, color: "#000" },
            }}
          />
        </div>
      </div>

      <div className={MOBILE_STYLES.footerContainer} data-testid="booking-policy-step-footer">
        <Button
          variant="contained"
          onClick={onNext}
          disabled={!agreed}
          fullWidth
          data-testid="booking-policy-step-next-btn"
          sx={{
            backgroundColor: "#2596fa",
            borderRadius: "5px",
            textTransform: "none",
            fontSize: "16px",
            fontWeight: 500,
            height: "40px",
            "&:hover": { backgroundColor: "#1e7cd8" },
            "&.Mui-disabled": { backgroundColor: "#2596fa", opacity: 0.5 },
          }}
        >
          {i18n.t("app.public.venue.modal.next")}
        </Button>
        <Button
          variant="outlined"
          onClick={onBack}
          fullWidth
          data-testid="booking-policy-step-back-btn"
          sx={{
            borderColor: "#eaeaea",
            borderRadius: "5px",
            textTransform: "none",
            fontSize: "16px",
            fontWeight: 500,
            height: "34px",
            color: "#545a65",
            backgroundColor: "#ffffff",
            "&:hover": { borderColor: "#d1d5db", backgroundColor: "#ffffff" },
          }}
        >
          {i18n.t("app.public.venue.modal.back")}
        </Button>
        <div className="flex justify-center">
          <Logo variant="wordmark" className="h-[10px] w-auto" aria-label="tabb" />
        </div>
      </div>
    </div>
  )
}

const BookingPolicyStep: React.FC<BookingPolicyStepProps> = (props) => {
  if (props.mobileSheet) {
    return <BookingPolicyStepMobile {...props} />
  }
  return <BookingPolicyStepDesktop {...props} />
}

export default BookingPolicyStep
